import { useEffect, useState } from 'react';
import { ScreenFrame } from '@/components/Screen';
import { CopyField, KeyValueGrid, SectionCard } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { account, auth } from '@/api/endpoints';
import { errorMessage, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import type { SessionUser } from '@/auth/session';
import {
  Button,
  ErrorBox,
  Form,
  Loading,
  Money,
} from '@/components/ui';
import { PanelInput } from '@/components/admin/Panel';
import { required, str } from '@/utils/format';

export function AccountScreen({
  user,
  onUser,
  mode = 'customer',
}: {
  user: SessionUser;
  onUser: (u: SessionUser) => void;
  mode?: 'admin' | 'customer';
}) {
  const me = useAsync(
    () => auth.me(),
    (p) => (p.user && typeof p.user === 'object' ? (p.user as SessionUser) : user),
  );

  useEffect(() => {
    if (me.data) onUser(me.data);
  }, [me.data, onUser]);

  const apiInfo = useAsync(
    () => account.apiInfo(),
    (p) =>
      p.data && typeof p.data === 'object' && !Array.isArray(p.data) ? (p.data as JsonMap) : p,
  );

  const pinStatus = useAsync(
    () => account.transactionPin(),
    (p) =>
      p.data && typeof p.data === 'object' && !Array.isArray(p.data) ? (p.data as JsonMap) : p,
  );

  const [curPass, setCurPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [pin, setPin] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [revealedApiToken, setRevealedApiToken] = useState('');

  const profile = me.data ?? user;

  async function changePassword() {
    const miss = required({ curPass, newPass }, ['curPass', 'newPass']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    const res = await account.changePassword({
      current_password: curPass,
      new_password: newPass,
    });
    setBusy(false);
    setMsg(res.success ? 'تم تغيير كلمة المرور.' : errorMessage(res));
    if (res.success) {
      setCurPass('');
      setNewPass('');
    }
  }

  async function setTxnPin() {
    const miss = required({ pin }, ['pin']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    const res = await account.setTransactionPin({ pin, transaction_pin: pin });
    setBusy(false);
    setMsg(res.success ? 'تم ضبط رمز العمليات.' : errorMessage(res));
    if (res.success) {
      setPin('');
      pinStatus.reload();
    }
  }

  async function removeTxnPin() {
    setBusy(true);
    const res = await account.removeTransactionPin({ pin });
    setBusy(false);
    setMsg(res.success ? 'تم إزالة رمز العمليات.' : errorMessage(res));
    pinStatus.reload();
  }

  async function regenApi() {
    setBusy(true);
    const res = await account.regenerateApi();
    setBusy(false);
    if (res.success) {
      const data =
        res.data && typeof res.data === 'object' && !Array.isArray(res.data)
          ? (res.data as JsonMap)
          : (res as JsonMap);
      setRevealedApiToken(str(data.token, ''));
      setMsg('تم تجديد مفتاح API. احفظه الآن؛ لن يُعرض بالكامل مرة أخرى.');
    } else {
      setMsg(errorMessage(res));
    }
    apiInfo.reload();
  }

  if (me.loading) return <Loading />;
  if (me.error) return <ErrorBox message={me.error} onRetry={me.reload} />;

  const apiEnabled = apiInfo.data?.enabled;
  const apiStatus =
    apiEnabled === true || apiEnabled === 1 || apiEnabled === '1'
      ? 'مفعّل'
      : apiEnabled === false || apiEnabled === 0 || apiEnabled === '0'
        ? 'غير مفعّل'
        : str(apiInfo.data?.access ?? apiInfo.data?.status);
  const apiPrefix = str(apiInfo.data?.token_prefix, '');
  const apiKey =
    str(revealedApiToken || apiInfo.data?.api_key || apiInfo.data?.token, '') ||
    (apiPrefix ? `${apiPrefix}…` : '');

  return (
    <ScreenFrame
      mode={mode}
      icon="user"
      title="حسابي"
      subtitle="الملف الشخصي، الأمان، رمز المحفظة، ومفاتيح API."
    >
      {msg ? <p className={msg.includes('تم') ? 'ok' : 'error'}>{msg}</p> : null}

      <SectionCard title="الملف الشخصي" icon={<Icon name="user" size={24} />}>
        <KeyValueGrid
          items={[
            { label: 'الاسم', value: str(profile.name) },
            { label: 'الهاتف', value: str(profile.phone) },
            { label: 'الدور', value: str(profile.role) },
            { label: 'رصيد الوحدات', value: <Money value={profile.wallet_balance} /> },
            {
              label: 'رصيد الألعاب',
              value: (
                <Money
                  value={profile.game_wallet_balance ?? profile.games_wallet_balance}
                  suffix="$"
                />
              ),
            },
            { label: 'العضوية', value: str(profile.membership_name ?? profile.membership_id) },
          ]}
        />
      </SectionCard>

      <SectionCard title="تغيير كلمة المرور" subtitle="من 10 إلى 128 حرفاً.">
        <Form className="password-form" onSubmit={() => void changePassword()}>
          <PanelInput
            label="كلمة المرور الحالية"
            type="password"
            value={curPass}
            onChange={(e) => setCurPass(e.target.value)}
            required
          />
          <PanelInput
            label="كلمة المرور الجديدة"
            hint="من 10 إلى 128 حرفاً."
            type="password"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            required
          />
          <div className="action-row wrap">
            <Button type="submit" disabled={busy}>
              حفظ كلمة المرور
            </Button>
          </div>
        </Form>
      </SectionCard>

      <SectionCard title="رمز عمليات المحفظة" subtitle="يحمي عمليات المحفظة الحساسة.">
        <p className="field-hint">
          الحالة: {pinStatus.data?.enabled || pinStatus.data?.has_pin ? 'مفعّل' : 'غير مفعّل'}
        </p>
        <Form onSubmit={() => void setTxnPin()}>
          <PanelInput
            label="PIN (4-6 أرقام)"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            inputMode="numeric"
            required
          />
          <div className="action-row wrap">
            <Button type="submit" disabled={busy}>
              ضبط الرمز
            </Button>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void removeTxnPin()}>
              إزالة الرمز
            </Button>
          </div>
        </Form>
      </SectionCard>

      <SectionCard title="Partner API" subtitle="مفتاح الربط للأنظمة الخارجية.">
        {apiInfo.loading ? (
          <Loading />
        ) : apiInfo.error ? (
          <ErrorBox message={apiInfo.error} onRetry={apiInfo.reload} />
        ) : (
          <div className="panel-stack tight">
            <KeyValueGrid
              items={[
                { label: 'الحالة', value: apiStatus },
                {
                  label: 'Base URL',
                  value: <code className="inline-code">{str(apiInfo.data?.base_url)}</code>,
                },
              ]}
            />
            <CopyField label="المفتاح" value={apiKey} badge="API" />
            {revealedApiToken ? (
              <p className="ok">انسخ المفتاح الآن. بعد مغادرة الصفحة لن يظهر إلا بادئة الرمز.</p>
            ) : null}
            <div className="action-row wrap">
              <Button type="button" variant="secondary" disabled={busy} onClick={() => void regenApi()}>
                تجديد المفتاح
              </Button>
            </div>
          </div>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
